import { DownArrowIcon, RightArrowIcon, UpArrowIcon } from '@/Components/Common/Icons';
import { useDashboardModal } from '@/Components/Dashboard/DashboardModal';
import { ContactSensorIcon, DoorClosedIcon, DoorOpenIcon } from '@/Components/Dashboard/icons';
import useGetUserEventsMutation from '@/Components/Hooks/useGetUserEventsMutation';
import {
  ACCESSORIES_PROPERTIES,
  DEVICE_EVENT_TYPES,
  DEVICE_EVENTS_PROPERTY,
  DEVICE_STATE_PROPERTY,
  DEVICE_TYPES,
  EVENT_PROPERTIES,
  EVENT_TYPES,
} from '@/constants/device';
import { translator } from '@vantiva-sa/homesight_humaui/Store/configStore';
import { Card } from 'antd';
import { get, isEmpty } from 'lodash';
import moment from 'moment';
import PropTypes from 'prop-types';
import { useCallback, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import '../RecentActivity.less';
import {
  EmptyModalContent,
  ErrorModalContent,
  formatTime,
  getDateLabel,
  LoadingCard,
  LoadingModalContent,
  sortByLatestTimestamp,
} from '../RecentActivityUtils';

const deviceTypes = [DEVICE_TYPES.DOOR_WINDOW_SENSOR];
const eventTypes = deviceTypes.flatMap((type) => DEVICE_EVENT_TYPES[type] || []);

const getDoorWindowState = (event = {}) => {
  const statePaths = [
    DEVICE_EVENTS_PROPERTY.accessory_state_update_door_window_sensor_open,
    DEVICE_STATE_PROPERTY.door_window_sensor,
    DEVICE_STATE_PROPERTY.door_window_sensor_ble,
    DEVICE_EVENTS_PROPERTY.accessory_state_ble_door_window_sensor,
  ];

  for (const path of statePaths) {
    const value = get(event, path, undefined);
    if (value !== undefined && value !== null && value !== '') {
      return value;
    }
  }

  return '';
};

const ModalContent = ({ timezone, is24HourFormat, locale, data, isLoading, isError }) => {
  const trans = useSelector(translator);
  if (isLoading) return <LoadingModalContent />;

  if (isError) return <ErrorModalContent />;

  if (isEmpty(data)) return <EmptyModalContent />;

  const rows = sortByLatestTimestamp(data);
  return (
    <div className="recent-blood-pressure-table-card">
      <div className="recent-blood-pressure-table-head">
        <div className="head-cell head-cell-date">{trans('DATE_L')}</div>
        <div className="head-cell head-cell-bp">{trans('LOCATION')}</div>
        <div className="head-cell">{trans('STATE')}</div>
      </div>

      <div className="recent-blood-pressure-table-body">
        {rows.map((entry, index) => {
          const timestamp = get(entry, EVENT_PROPERTIES.TIMESTAMP, '');
          const dateLabel = getDateLabel(timestamp, trans, timezone, locale);
          const formattedTime = formatTime(timestamp, is24HourFormat, timezone);
          const location = get(entry, EVENT_PROPERTIES.LOCATION, '-');
          const state = getDoorWindowState(entry);
          const stateLabel = state !== '' ? (state ? trans('OPEN') : trans('CLOSED')) : '-';
          const Icon = state !== '' ? (state ? DoorOpenIcon : DoorClosedIcon) : null;

          return (
            <div
              className="recent-blood-pressure-table-row"
              key={`recent-blood-pressure-table-row-${index}`}
            >
              <div className="row-cell date-cell">
                <span className="date-text">{dateLabel}</span>
                <span className="time-text">{formattedTime}</span>
              </div>

              <div className="row-cell bp-cell">{location}</div>
              <div className="row-cell bp-cell">
                {Icon && <Icon className="state-icon" />}
                {stateLabel}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

ModalContent.propTypes = {
  timezone: PropTypes.string,
  is24HourFormat: PropTypes.bool,
  locale: PropTypes.string,
  data: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.object), PropTypes.object]),
  isLoading: PropTypes.bool,
  isError: PropTypes.bool,
};

const SmartHomeDoorWindowCard = (props) => {
  const { useruuid, timezone, locale, is24HourFormat, accessories, data, isLoading, trendData } =
    props;
  const trans = useSelector(translator);
  const { openDashboardModal } = useDashboardModal();
  const modalDataApi = useGetUserEventsMutation();
  const latestModalRequestIdRef = useRef(0);
  const invalidateModalRequest = useCallback(() => (latestModalRequestIdRef.current += 1), []);

  const eventRequestMatrix = useMemo(() => {
    const requestMap = new Map();

    (accessories || [])
      .filter((a) => deviceTypes.includes(get(a, ACCESSORIES_PROPERTIES.DEVICE_TYPE, '')))
      .forEach((a) => {
        const deviceUuid = get(a, ACCESSORIES_PROPERTIES.DEVICE_UUID, '');
        const deviceType = get(a, ACCESSORIES_PROPERTIES.DEVICE_TYPE, '');
        if (!deviceUuid) return;

        const filteredEventTypes = eventTypes.filter((eventType) =>
          DEVICE_EVENT_TYPES[deviceType]?.includes(eventType)
        );
        if (isEmpty(filteredEventTypes)) return;

        requestMap.set(deviceUuid, {
          deviceuuid: deviceUuid,
          eventTypes: filteredEventTypes,
        });
      });

    return Array.from(requestMap.values());
  }, [accessories]);

  const latestEvent = useMemo(() => {
    if (isEmpty(data)) return {};
    const items = Array.isArray(data) ? data : [];
    const filteredData = items.filter((event) =>
      [
        EVENT_TYPES.DOOR_WINDOW_SENSOR.ACCESSORY_STATE_UPDATE_DOOR_WINDOW_SENSOR_OPEN,
        EVENT_TYPES.DOOR_WINDOW_SENSOR.ACCESSORY_STATE_BLE_DOOR_WINDOW_SENSOR,
      ].includes(get(event, EVENT_PROPERTIES.MESSAGE_TYPE, ''))
    );
    const sortedData = sortByLatestTimestamp(filteredData, EVENT_PROPERTIES.TIMESTAMP);
    return sortedData[0];
  }, [data]);

  const getModalConfig = useCallback(
    (data = [], isLoading = true, isError = false, onOpen, onClose = invalidateModalRequest) => ({
      title: (
        <div className="recent-activity-modal-title">
          <ContactSensorIcon className="recent-activity-modal-title-icon" />
          <span>{trans('CONTACT_SENSOR')}</span>
        </div>
      ),
      content: <ModalContent {...props} data={data} isLoading={isLoading} isError={isError} />,
      onOpen,
      onClose,
    }),
    [invalidateModalRequest, props, trans]
  );

  const fetchModalData = async (requestId) => {
    const startDate = moment().subtract(7, 'days').toISOString();
    const endDate = moment().toISOString();
    const shouldUpdateModal = requestId === latestModalRequestIdRef.current;

    if (isEmpty(eventRequestMatrix)) {
      if (shouldUpdateModal) {
        openDashboardModal(getModalConfig([], false, false));
      }
      return;
    }

    const commonPayload = { useruuid, startDate, endDate };

    const mutationRequests = eventRequestMatrix.map(({ deviceuuid, eventTypes }) =>
      modalDataApi.mutateAsync({
        ...commonPayload,
        deviceuuid,
        eventTypes,
      })
    );

    if (isEmpty(mutationRequests)) {
      if (shouldUpdateModal) {
        openDashboardModal(getModalConfig([], false, true));
      }
      return;
    }

    const mutationResults = await Promise.allSettled(mutationRequests);
    const canUpdateModal = requestId === latestModalRequestIdRef.current;
    if (!canUpdateModal) return;

    const mergedData = mutationResults.flatMap((result) =>
      result.status === 'fulfilled' ? result.value : []
    );
    const hasError = mutationResults.every((result) => result.status === 'rejected');
    openDashboardModal(getModalConfig(mergedData, false, hasError));
  };

  const handleCardClick = (e) => {
    e.preventDefault();
    const requestId = latestModalRequestIdRef.current + 1;
    latestModalRequestIdRef.current = requestId;
    openDashboardModal(getModalConfig([], true, false, () => fetchModalData(requestId)));
  };

  const handleCardKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      const requestId = latestModalRequestIdRef.current + 1;
      latestModalRequestIdRef.current = requestId;
      openDashboardModal(getModalConfig([], true, false, () => fetchModalData(requestId)));
    }
  };

  if (isLoading) return <LoadingCard />;

  const location = get(latestEvent, EVENT_PROPERTIES.LOCATION, '');
  const timestamp = get(latestEvent, EVENT_PROPERTIES.TIMESTAMP, '');
  const dateLabel = getDateLabel(timestamp, trans, timezone, locale);
  const formattedTime = formatTime(timestamp, is24HourFormat, timezone);
  const value = getDoorWindowState(latestEvent);
  const valueLabel = value !== '' ? (value ? trans('OPEN') : trans('CLOSED')) : '';

  const trendPercent = get(trendData, 'trendPercent', null);
  const yesterdayCount = get(trendData, 'yesterdayCount', null);
  const isUp = trendPercent > 0;
  const isDown = trendPercent < 0;
  const displayPercent = trendPercent !== null ? Math.abs(trendPercent) : null;

  const CardTitle = (
    <div className="recent-activity-card-header">
      <ContactSensorIcon className="recent-activity-card-icon" />
      <h4 className="recent-activity-card-title">{trans('CONTACT_SENSOR')}</h4>
    </div>
  );

  return (
    <Card
      className="recent-activity-card"
      onClick={handleCardClick}
      onKeyDown={handleCardKeyDown}
      role="button"
      tabIndex={0}
      hoverable
      variant="borderless"
      title={CardTitle}
    >
      <div className="recent-activity-card-body">
        <p className="recent-activity-time">
          {dateLabel} {formattedTime}
        </p>
        {location && <p className="recent-activity-context">{location}</p>}
        <p className="recent-activity-data-value">{valueLabel}</p>
      </div>
      <>
        <hr className="recent-activity-divider" />
        <div className="recent-activity-footer-section">
          <p className="recent-activity-footer-label">
            {yesterdayCount !== null
              ? `${trans('YESTERDAY_OPENED')} ${yesterdayCount} ${trans('TIMES')}`
              : trans('YESTERDAY_OPENED_EMPTY')}
          </p>
          <div className="recent-activity-card-footer">
            <span className="recent-activity-comparison">
              {trans('YESTERDAY_VS_LAST_WEEK_AVG')}
            </span>
            {displayPercent !== null ? (
              <span
                className={`recent-activity-trend ${isUp ? 'up' : isDown ? 'down' : 'neutral'}`}
              >
                {isUp ? (
                  <UpArrowIcon className="trend-arrow" />
                ) : isDown ? (
                  <DownArrowIcon className="trend-arrow" />
                ) : (
                  <RightArrowIcon className="trend-arrow" />
                )}
                <span>{displayPercent}%</span>
              </span>
            ) : (
              <span className="recent-activity-trend neutral">-</span>
            )}
          </div>
        </div>
      </>
    </Card>
  );
};

SmartHomeDoorWindowCard.propTypes = {
  useruuid: PropTypes.string,
  timezone: PropTypes.string,
  is24HourFormat: PropTypes.bool,
  locale: PropTypes.string,
  accessories: PropTypes.arrayOf(PropTypes.object),
  data: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.object), PropTypes.object]),
  isLoading: PropTypes.bool,
  trendData: PropTypes.object,
};

export default SmartHomeDoorWindowCard;
